/*
 *
 *  * Copyright (c) 2020 General Electric Company. All rights reserved.
 *  *
 *  * The copyright to the computer software herein is the property of
 *  * General Electric Company. The software may be used and/or copied only
 *  * with the written permission of General Electric Company or in accordance
 *  * with the terms and conditions stipulated in the agreement/contract
 *  * under which the software has been supplied.
 *
 */
import { Observable, Subject } from 'rxjs';
import { ModalComponent } from './modal.component';

export class ModalRef {
  private readonly _afterOpened = new Subject<any>();
  private readonly _afterClosed = new Subject<any>();
  private readonly _afterVisibilityChange = new Subject<boolean>();
  private readonly _afterOutsideClicked = new Subject<boolean>();

  readonly afterOpened: Observable<any> = this._afterOpened.asObservable();
  readonly afterClosed: Observable<any> = this._afterClosed.asObservable();

  /**
   * Observable which emits value true for Modal is visible and false if hidden.
   * @returns `boolean` - A boolean indicating whether the modal is visible or hidden.
   */
  readonly afterVisibilityChange: Observable<boolean> = this._afterVisibilityChange.asObservable();
  /**
   * Observable which emits value true when a click is trigerred outside of the modal.
   * @returns `boolean` - A boolean indicating click is trigerred outside of the modal.
   */
  readonly afterOutsideClicked: Observable<boolean> = this._afterOutsideClicked.asObservable();

  private instance: ModalComponent = null;

  close(result?: any) {
    this._afterClosed.next(result);
    this._afterClosed.complete();
    this._afterVisibilityChange.complete();
    this._afterOutsideClicked.complete();
  }

  /**
   * Updates the afterOutsideClicked subject of the modal to emit afterOutsideClicked observable
   * with true value,
   * Use it for trigerring manual outside click
   * If the modal is configured as a block modal or a decision modal, this method will not
   * trigger outside click
   */
  public triggerOutsideClick() {
    const { isBlockModal, isDecisionModal } = this.instance.config;
    if (isBlockModal || isDecisionModal) {
      return;
    }
    this._afterOutsideClicked.next(true);
  }

  /**
   * Updates the visibility state of the modal.
   *
   * @param visible - A boolean indicating whether the modal should be visible or hidden.
   *
   * If the modal is configured as a block modal or a decision modal, this method will not
   * change the visibility state
   */
  setVisibility(visible: boolean) {
    const { isBlockModal, isDecisionModal } = this.instance.config;
    if (isBlockModal || isDecisionModal) {
      return;
    }
    this.instance.setVisibility(visible);
    this._afterVisibilityChange.next(visible);
  }

  opened() {
    this._afterOpened.next();
    this._afterOpened.complete();
  }
}
